'use client';

import { createClient } from '@supabase/supabase-js';
import { env } from '@repo/env';
import type { Database } from '../types/database.types';

if (!env.supabase?.url || !env.supabase?.anonKey) {
  console.error('[Supabase] Missing environment variables');
  throw new Error('Missing environment variables for Supabase configuration');
}

const supabaseUrl = env.supabase.url;
const supabaseAnonKey = env.supabase.anonKey;

// Create storage handler with fallback memory storage
const createStorageAdapter = () => {
  // Shared memory storage instance
  const memoryStorage = new Map<string, string>();
  
  // Memory storage adapter implementation
  const memoryStorageAdapter = {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStorage.set(key, value);
    },
    removeItem: (key: string) => {
      memoryStorage.delete(key);
    },
  };

  // Check if we're in a browser context
  const isBrowser = typeof window !== 'undefined';
  
  if (!isBrowser) {
    console.debug('[Supabase] Server context detected, using memory storage');
    return memoryStorageAdapter;
  }
  
  try {
    // Test if localStorage is available and accessible
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('supabase.test', 'test');
      localStorage.removeItem('supabase.test');
      console.debug('[Supabase] Using localStorage');
      return localStorage;
    }
    throw new Error('localStorage is undefined');
  } catch (error) {
    console.warn('[Supabase] localStorage not available, using memory storage:', error);
    return memoryStorageAdapter;
  }
};

const createWebConfig = () => ({
  auth: {
    storage: createStorageAdapter(),
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce' as const,
    debug: process.env.NODE_ENV === 'development',
    // Add cookie options for better SSR support
    storageOptions: {
      cookie: {
        name: 'sb-auth-token',
        lifetime: 60 * 60 * 24 * 7, // 1 week
        domain: '',
        path: '/',
        sameSite: 'lax'
      }
    }
  },
  global: {
    headers: { 'x-client-info': '@repo/shared' }
  }
});

// Singleton instance with better SSR handling
const globalForSupabase = globalThis as unknown as {
  supabase: ReturnType<typeof createClient<Database>> | undefined;
};

export const getSupabaseClient = () => {
  if (globalForSupabase.supabase) {
    return globalForSupabase.supabase;
  }

  globalForSupabase.supabase = createClient<Database>(
    supabaseUrl,
    supabaseAnonKey,
    createWebConfig()
  );

  return globalForSupabase.supabase;
};

// Export a single instance
export const supabase = getSupabaseClient();

// Debug logging in development
if (process.env.NODE_ENV === 'development') {
  console.debug('[Supabase] Initialized with URL:', supabaseUrl);
} 